import { Loader2, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { QuoteFeeRatesCard } from "@/components/features/platform/quote-fee-rates-card";
import { QuoteDeliveryRateCard } from "@/components/features/platform/quote-delivery-rate-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { certificateProductRowToUpsertBody } from "@/pages/platform/certificate-product-form-submit";
import { CertificatePricingProductsTable } from "@/pages/platform/certificate-pricing-products-table";
import {
  type CertificateProductPriceRow,
  getUrSnapshot,
  listCertificateProductPrices,
  upsertCertificateProductPrice,
} from "@/services/platformCertificatePricingService";

const QUERY_KEY = ["platform", "products"] as const;

const ACTIVE_STATUS_PARAM = "active";

type ActiveStatusFilter = "all" | "active" | "inactive";

function parseActiveStatusParam(raw: string | null): ActiveStatusFilter {
  if (raw === "active" || raw === "inactive") return raw;
  return "all";
}

export function CertificatePricingPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pagination = usePaginationFromUrl();
  const [togglingProductId, setTogglingProductId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStatusFilter = parseActiveStatusParam(searchParams.get(ACTIVE_STATUS_PARAM));

  const setActiveStatusFilter = useCallback(
    (value: ActiveStatusFilter): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "all") {
            next.delete(ACTIVE_STATUS_PARAM);
          } else {
            next.set(ACTIVE_STATUS_PARAM, value);
          }
          next.set("page", "1");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listCertificateProductPrices,
  });

  const urQuery = useQuery({
    queryKey: [...QUERY_KEY, "ur-snapshot"],
    queryFn: getUrSnapshot,
  });

  const rows = listQuery.data ?? [];

  const filteredRows = useMemo((): CertificateProductPriceRow[] => {
    if (activeStatusFilter === "all") return rows;
    if (activeStatusFilter === "active") return rows.filter((r) => r.isActive);
    return rows.filter((r) => !r.isActive);
  }, [rows, activeStatusFilter]);

  useEffect(() => {
    pagination.setTotal(filteredRows.length);
  }, [filteredRows.length, pagination]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filteredRows.slice(start, start + pagination.limit);
  }, [filteredRows, pagination.page, pagination.limit]);

  const goToEdit = useCallback(
    (row: CertificateProductPriceRow): void => {
      navigate(`/products/${row.id}`);
    },
    [navigate],
  );

  const toggleActiveMutation = useMutation({
    mutationFn: upsertCertificateProductPrice,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(
        variables.isActive
          ? t("platform.products.toggleActivatedToast")
          : t("platform.products.toggleDeactivatedToast"),
      );
    },
    onError: () => {
      toast.error(t("platform.products.saveError"));
    },
    onSettled: () => {
      setTogglingProductId(null);
    },
  });

  const handleToggleActive = useCallback(
    (row: CertificateProductPriceRow): void => {
      setTogglingProductId(row.id);
      toggleActiveMutation.mutate({
        ...certificateProductRowToUpsertBody(row),
        isActive: !row.isActive,
      });
    },
    [toggleActiveMutation],
  );

  const dgiUrFormatted = useMemo(() => {
    const s = urQuery.data?.dgi;
    if (!s || s.urValueMinor == null) return null;
    return (s.urValueMinor / 100).toLocaleString("es-UY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [urQuery.data]);

  const cajaNotarialUrFormatted = useMemo(() => {
    const s = urQuery.data?.cajaNotarial;
    if (!s || s.urValueMinor == null) return null;
    return (s.urValueMinor / 100).toLocaleString("es-UY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [urQuery.data]);

  const handleRefresh = useCallback((): void => {
    void listQuery.refetch();
    void urQuery.refetch();
  }, [listQuery, urQuery]);

  const isRefreshing = listQuery.isFetching || urQuery.isFetching;
  const columnCount = 10;

  if (listQuery.isLoading) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t("platform.products.title")} />
        <TableCard className="mt-4">
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        </TableCard>
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t("platform.products.title")} />
        <TableCard className="mt-4">
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6 py-12">
            <EmptyState
              title={t("platform.products.loadError")}
              action={
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void listQuery.refetch()}
                  className="transition-colors duration-150"
                >
                  {t("platform.products.retryLoad")}
                </Button>
              }
            />
          </div>
        </TableCard>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <PageHeader title={t("platform.products.title")} />

      <QuoteFeeRatesCard />
      <QuoteDeliveryRateCard />

      <Card className="mt-4 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                {t("platform.products.urSnapshotTitle")}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("platform.products.urSnapshotHint")}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void urQuery.refetch()}
              disabled={urQuery.isFetching}
              className="shrink-0 transition-colors duration-150"
            >
              {urQuery.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("platform.products.refreshUr")}
            </Button>
          </div>
          {urQuery.data ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("platform.products.urSnapshotTitle")}
                </h3>
                <dl className="mt-2 grid gap-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">
                      {t("platform.products.urReferencePeriod")}
                    </dt>
                    <dd className="font-medium text-foreground">
                      {urQuery.data.dgi.referenceLabel}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t("platform.products.urValue")}</dt>
                    <dd className="font-medium text-foreground">
                      {dgiUrFormatted != null
                        ? `$ ${dgiUrFormatted}`
                        : t("platform.products.urUnavailable")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t("platform.products.urSource")}</dt>
                    <dd className="font-medium text-foreground">{urQuery.data.dgi.source}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("platform.products.cajaNotarialUrTitle")}
                </h3>
                <dl className="mt-2 grid gap-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">
                      {t("platform.products.urReferencePeriod")}
                    </dt>
                    <dd className="font-medium text-foreground">
                      {urQuery.data.cajaNotarial.referenceLabel ?? "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t("platform.products.urValue")}</dt>
                    <dd className="font-medium text-foreground">
                      {cajaNotarialUrFormatted != null
                        ? `$ ${cajaNotarialUrFormatted}`
                        : t("platform.products.urUnavailable")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t("platform.products.urSource")}</dt>
                    <dd className="font-medium text-foreground">
                      {urQuery.data.cajaNotarial.source}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="flex w-full max-w-xs flex-col gap-2 sm:w-auto">
          <Label htmlFor="products-active-filter" className="text-foreground">
            {t("platform.products.filterStatusLabel")}
          </Label>
          <Select
            value={activeStatusFilter}
            onValueChange={(v) => setActiveStatusFilter(parseActiveStatusParam(v))}
          >
            <SelectTrigger
              id="products-active-filter"
              className="w-full transition-colors duration-150 sm:w-[200px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("platform.products.filterStatusAll")}</SelectItem>
              <SelectItem value="active">{t("platform.products.filterStatusActive")}</SelectItem>
              <SelectItem value="inactive">{t("platform.products.filterStatusInactive")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          onClick={() => navigate("/products/new")}
          className="shrink-0 transition-colors duration-150 sm:ml-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("platform.products.addProduct")}
        </Button>
      </div>

      <CertificatePricingProductsTable
        rows={filteredRows}
        totalUnfilteredCount={rows.length}
        paginatedRows={paginatedRows}
        pagination={pagination}
        columnCount={columnCount}
        togglingProductId={togglingProductId}
        onEdit={goToEdit}
        onToggleActive={handleToggleActive}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
    </div>
  );
}
